import { ModalService } from '@proximus/ng-common-components';
import {
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import {
  getFirstJointOffer,
  InternetProduct,
  OverviewCardType,
  PlcCodes,
  Product,
  ProductTypes,
} from '@dch/products-business-logic';
import { Store } from '@ngrx/store';
import { ApplicationState } from '../../../store/reducers';
import {
  EnvironmentService,
  InceptionService,
  TranslationService,
} from '@proximus/ng-common';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { getUsageProductFrom } from 'src/app/shared/usage/util';
import { Product as UsageProduct } from '@proximus/usage-business-logic';
import {
  getPiiServiceId,
  savePii,
  savePiiBrand,
  savePiiCloudServiceId,
  savePiiMyMobile,
  savePiiNsp,
} from '../../../shared/pii/pii.utils';
import { Router } from '@angular/router';
import { getOverviewProductType, isMpcuidBlacklisted } from '../../util/overview-util';
import { buildTaggingProduct } from '../../../tagging/tagging.utils';
import { getInternetProductDescription } from '../../../shared/giga-pack-speed/giga-pack-speed';
import { DISNEY_PLUS_PREMIUM } from 'src/app/details/constants';
import { AlwaysProtectedCheckService } from '../../../services/always-protected-check.service';
import { NortonProductCheckService } from '../../../services/norton-product-check.service';
import { EASY_PROTECT_NAME } from 'src/app/always-protected-check.constants';
import { Subscription } from 'rxjs';
import { EwebTokenService } from '../../../services/eweb-token.service';

declare let inception;

@Component({
  selector: 'mprd-card',
  templateUrl: './card.component.html',
})
export class CardComponent implements OnInit, OnChanges {
  @Input() product: Product;
  @Input() cardType: OverviewCardType;
  @Input() isMyMobile: boolean;
  @Input() outOfPackCardZoneTagging: string;
  @Input() isAllowedToRedirectToEshare = true;

  internetDescription: string;
  disneyPlusPremiumCode = DISNEY_PLUS_PREMIUM;

  // Plc allowed to display 'Add or remove options'
  private allowedPlcGotoOptions: Array<PlcCodes> = [
    PlcCodes.MV,
    PlcCodes.MMV,
    PlcCodes.MI,
    PlcCodes.TV,
    PlcCodes.FI,
  ];

  // (DNS, digitalis, shared hosting, Norton new and old, Outlook 365)
  public nonEligibleUsagePlcs: Array<PlcCodes> = [
    <any>'DNS',
    <any>'WBD',
    <any>'HST',
    <any>'NIS',
    <any>'SEC',
    <any>'O365',
  ];
  productType: string;
  serviceId: string;
  hasUsage = false;
  hasGoToOptionsItem = false;
  OverviewCardType = OverviewCardType;
  PlcCodes = PlcCodes;

  cardClickedEvent: any;

  usageProduct: UsageProduct;

  isProductOfferEligibleForPenalty = false;
  isExceedingAmountShown = true;
  // tagging
  jsonProduct: any;
  isAlwaysProtected: boolean;
  easyProtectOptions = [];
  hasEasyProtect = false;
  isFeatureFlag: boolean;
  easyProtectValue: Subscription;
  constructor(
    private store: Store<ApplicationState>,
    private inceptionService: InceptionService,
    private modalService: ModalService,
    private translationService: TranslationService,
    private elementRef: ElementRef,
    private router: Router,
    private environmentService: EnvironmentService,
    private alwaysProtectedValue: AlwaysProtectedCheckService,
    private readonly nortonProductCheck: NortonProductCheckService,
    private readonly ewebTokenService: EwebTokenService,
  ) {}

  ngOnInit(): void {
    this.initialize();
    this.isExceedingAmountShown =
      (
        this.translationService.getTranslatedValueFor(
          'products.overview.Ebu.exceedingBillNotShown.productIds',
        ) ?? 'false'
      )
        .split(',')
        .indexOf(this.product.productId) === -1;
    this.isFeatureFlag =
      (this.translationService.getTranslatedValueFor('tv.isFeatureFlag') ??
        'false') === 'true';
    this.jsonProduct = buildTaggingProduct(this.product);
    this.isProductOfferEligibleForPenalty = !!getFirstJointOffer(this.product)
      ?.isProductOfferEligibleForPenalty;
    this.alwaysProtectedValue.currentAlwaysProtected.subscribe((data) => {
      this.isAlwaysProtected = data;
      this.updateEasyProtectStatus();
    });
  }

  private updateEasyProtectStatus(): void {
    if (this.isAlwaysProtected && this.product) {
      this.hasEasyProtect = this.product?.activeOptions?.some(
        (option) => option.technicalName === EASY_PROTECT_NAME,
      );
      this.easyProtectValue =
        this.alwaysProtectedValue.alwaysProtectedRes?.subscribe((data) => {
          this.easyProtectOptions = data;
        });
    } else {
      this.hasEasyProtect = false;
    }
  }
  private initialize() {
    this.productType = getOverviewProductType(
      this.product.plc,
      this.product.labelIdentifier,
    );
    this.serviceId = getPiiServiceId();

    this.cardClickedEvent = {
      event_detail: this.product.productDescription,
      event_location: 'myproducts.overview',
      event_action: 'expand',
    };

    if (this.product.plc === PlcCodes.FI) {
      this.internetDescription = getInternetProductDescription(
        this.product as InternetProduct,
      );
    }

    this.hasGoToOptionsItem =
      this.product &&
      this.productType !== 'FON' &&
      this.allowedPlcGotoOptions.includes(this.product.plc);
  }

  ngOnChanges(): void {
    this.hasUsage = this.hasUsageDirective();
    if (this.product) {
      this.usageProduct = getUsageProductFrom(this.product);
    }
  }

  private hasUsageDirective(): boolean {
    return this.nonEligibleUsagePlcs.indexOf(this.product.plc) === -1;
  }

  redirectToOptionsSection(event) {
    event.stopPropagation();
    savePii(this.product.accessNumber);
    const isEshareTvOptionFlowEnabled =
      this.translationService.getTranslatedValueFor(
        'eshare.tv.enableOptionFlowRedirect',
      ) === 'true';
    const isEshareMobileOptionFlowEnabled =
      this.translationService.getTranslatedValueFor(
        'eshare.mobile.enableOptionFlowRedirect',
      ) === 'true';
    const isBlacklisted = isMpcuidBlacklisted(
      this.product.productId,
      this.translationService.getTranslatedValueFor(
        'eshare.optionFlow.mpcuidBlacklist',
      ),
    );

    if (this.productType === 'TV' && isEshareTvOptionFlowEnabled && !isBlacklisted) {
      this.ewebTokenService.getRedirectToEshareOptionFlowUrl(PlcCodes.TV, null, this.product.accessNumber).subscribe((redirectUrl: string) => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      });
    } else if (this.productType === 'MOBILE' && isEshareMobileOptionFlowEnabled && this.isAllowedToRedirectToEshare && !isBlacklisted) {
      this.ewebTokenService.getRedirectToEshareOptionFlowUrl(PlcCodes.MV, null, this.product.accessNumber).subscribe((redirectUrl: string) => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      });
      return;
    } else {
      this.router.navigate(['details/' + this.productType.toLowerCase()], {
        fragment: 'available-options',
      });
    }
  }

  public openCard(event): void {
    if (this.product.productReadOnly) {
      event.stopPropagation();
    }
    if (this.productType) {
      this.openCardByProductType();
    } else if (this.product?.plc) {
      this.openCardByPlc();
    }
  }

  private openCardByProductType(): void {
    if (this.productType === 'MAIL') {
      this.router.navigate(['details/mail']);
      return;
    }
    savePii(this.product.accessNumber);
    savePiiBrand(this.product.brand);
    savePiiMyMobile(this.product.myMobile);
    switch (this.productType) {
      case 'CLOUD':
        savePiiCloudServiceId(this.product.accessNumber);
        this.router.navigate(['/' + this.productType.toLowerCase()]);
        break;
      case 'NORTON':
        this.handleNortonRedirection();
        break;
      case 'FON':
        if (this.environmentService.isProximusPlusMobileApp) {
          this.router.navigate(['/pxsplus/public-wifi']);
        } else {
          this.router.navigate(['/public-wifi']);
        }
        break;
      default:
        this.router.navigate(['details/' + this.productType.toLowerCase()]);
        break;
    }
  }

  private openCardByPlc(): void {
    const plc = this.product.plc;
    if (plc === PlcCodes.WBD) {
      savePii(this.product.accessNumber);
      this.router.navigate(['details/digitalis']);
    } else if (plc === PlcCodes.FM) {
      window.open(
        this.translationService.getTranslatedValueFor(
          'products.overview.family.life.premium.link',
        ),
        '_self',
      );
    } else if (
      [PlcCodes.NIS, PlcCodes.O365].includes(plc) ||
      (plc === PlcCodes.DV && this.product.dtc === 'FE')
    ) {
      this.openProductModal();
    } else if (plc === PlcCodes.NSP) {
      savePiiNsp(this.buildNspPayload());
      this.router.navigate(['/nspManage']);
    } else if (plc !== PlcCodes.LDM) {
      window.open(
        this.product.detailUrl,
        plc === PlcCodes.PREMIUM_NUMBERS ? '_blank' : '_self',
      );
    }
  }

  private openProductModal(): void {
    this.modalService
      .create(ModalComponent)
      .subscribe((componentRef: ComponentRef<ModalComponent>) => {
        this.getTranslations(componentRef);
        componentRef.instance.plc = this.product.plc;
      });
  }

  private buildNspPayload(): any {
    if (this.product.packId) {
      return {
        packId: this.product.packId,
      };
    }
    if (this.product.addressKey) {
      return {
        productId: this.product.productId,
        plcList: [PlcCodes.FI],
      };
    }
    return {
      plcList: [PlcCodes.MV, PlcCodes.MI],
    };
  }

  private getTranslations(componentRef: ComponentRef<ModalComponent>) {
    if (this.product.plc === PlcCodes.O365) {
      this.defineContent(
        componentRef,
        'products.overview.o365-phase-out.title',
        'products.overview.o365-phase-out.text',
      );
    } else if (this.product.plc === PlcCodes.FT) {
      this.defineContent(
        componentRef,
        'products.overview.phoneLine-phase-out.title',
        'products.overview.phoneLine-phase-out.text',
      );
    }
  }

  private defineContent(
    componentRef: ComponentRef<ModalComponent>,
    title: string,
    text: string,
  ) {
    componentRef.instance.title =
      this.translationService.getTranslatedValueFor(title);
    componentRef.instance.text =
      this.translationService.getTranslatedValueFor(text);
  }

  public scrollToTop(): void {
    setTimeout(() => {
      this.inceptionService.scrollToElement(this.elementRef);
    });
  }

  usageInitialized(isInitialized: boolean) {
    if (isInitialized) {
      setTimeout(() => inception.masonry('[data-masonry-container]'));
    }
  }

  handleLDMLinks(target: string) {
    switch (target) {
      case 'manage': {
        window.open(
          this.translationService.getTranslatedValueFor(
            'products.overview.ldm.manageYourWebsite.link',
          ),
          '_blank',
        );
        break;
      }
      case 'addProducts': {
        window.open(
          this.translationService.getTranslatedValueFor(
            'products.overview.ldm.addProducts.link',
          ),
          '_self',
        );
        break;
      }
      case 'changeSubscription': {
        this.modalService
          .create(ModalComponent)
          .subscribe((componentRef: ComponentRef<ModalComponent>) => {
            this.getTranslations(componentRef);
            componentRef.instance.plc = this.product.plc;
          });
        break;
      }
    }
  }

  extractLDMDomainName() {
    return [
      this.product.ldmBusinessLocation,
      this.product.ldmDomainName,
      this.product.ldmCampaignName,
    ].find((productName) => productName);
  }

  clickAddDataButtonCallback = () => {
    const product = this.product;
    if (
      product.type === ProductTypes.INTERNET ||
      product.type === ProductTypes.MOBILE_BONE ||
      product.type === ProductTypes.MOBILE_MCS
    ) {
      this.router.navigate(['details/' + this.productType.toLowerCase()], {
        fragment: 'available-options',
      });
    }
    // eslint-disable-next-line @typescript-eslint/semi, @typescript-eslint/member-delimiter-style
  };

  handleNortonRedirection = () => {
    const newNortonDetailPageMpcuuids = ['PMBDV', 'CPZJA'];
    if (newNortonDetailPageMpcuuids.includes(this.product.groupMpcUuid)) {
      this.router.navigate(['/norton360']);
    } else {
      this.nortonProductCheck.checkForNortonName(
        this.product.productDescription,
      );
      this.router.navigate([
        '/' + this.productType.toLowerCase() + '/' + this.product.groupMpcUuid,
      ]);
    }
  };
  easyProtectOption: any;

  navigateToMyJointOffer() {
    savePii(this.product.jointOffers[0].serviceId);
    this.router.navigate(['/details/mobile/joint-offer']);
  }
}
